import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../api";
import { navigate } from "../router";

const links = [
  { href: "/", label: "Inbox" },
  { href: "/cleaners", label: "Cleaners" },
  { href: "/contracts", label: "Contracts" },
  { href: "/website", label: "Website" },
];

interface Props {
  path: string;
  email: string | undefined;
  onSignOut: () => void;
}

/**
 * The sidebar on a laptop; a hamburger and a drawer on a phone.
 *
 * It was a horizontally scrolling strip, which is the pattern that hides
 * whatever does not fit and gives no sign that anything is missing. Three
 * sections is few enough that a drawer shows all of them at once, at a size
 * worth tapping.
 */
export function Nav({ path, email, onSignOut }: Props) {
  const [open, setOpen] = useState(false);

  const { data: summary } = useQuery({
    queryKey: ["summary"],
    queryFn: api.summary,
  });

  // Only unattended counts are badged. A badge on every item is wallpaper; a
  // badge that means "this needs you" is a signal.
  const badges: Record<string, number | undefined> = {
    "/": summary?.inquiries.new,
    "/cleaners": summary?.leads.supplyNew,
    "/contracts": summary?.leads.demandNew,
    "/website": undefined,
  };

  // Navigating closes the drawer, including via the back button -- a drawer left
  // open over the page you just moved to is the classic mobile-nav bug.
  useEffect(() => setOpen(false), [path]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const isCurrent = (href: string) =>
    path === href || (href !== "/" && path.startsWith(href));

  return (
    <nav className={`nav ${open ? "nav--open" : ""}`}>
      <div className="nav__bar">
        <div className="nav__brand">
          <span className="nav__mark">HC</span>
          <span>Admin</span>
        </div>

        <button
          className="nav__toggle"
          aria-expanded={open}
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
        >
          <span aria-hidden="true">{open ? "✕" : "☰"}</span>
        </button>
      </div>

      {/* Collapsed with display:none on narrow screens rather than an `inert`
          attribute: inert would key off `open`, which is false on a laptop too,
          and would quietly disable the permanent sidebar. display:none also
          takes the links out of tab order for free. */}
      <div className="nav__panel">
        <ul className="nav__links">
          {links.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                aria-current={isCurrent(link.href) ? "page" : undefined}
                onClick={(e) => {
                  // Let modified clicks open a new tab as usual.
                  if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                  e.preventDefault();
                  navigate(link.href);
                  setOpen(false);
                }}
              >
                {link.label}
                {badges[link.href] ? (
                  <span className="nav__badge">{badges[link.href]}</span>
                ) : null}
              </a>
            </li>
          ))}
        </ul>

        <div className="nav__footer">
          {email && (
            <p className="nav__email" title={email}>
              {email}
            </p>
          )}
          <button className="btn btn--ghost btn--sm" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </nav>
  );
}
